/**
 * Section constants for Family History v2. Relatives themselves come from
 * api-family-history-v2-service — this file holds the enum vocabularies used
 * by the modal (Relationship dropdown, Common conditions quick-picks) and a
 * label lookup for the table.
 */

/**
 * Relationship dropdown — backend enum keys + display labels. "other" is the
 * catch-all with a free-text `customRelation` (Uncle / Aunt / Cousin / Spouse
 * all funnel through it).
 *
 * Order below is the intended display order in the dropdown and drives the
 * table sort implicitly when the caller iterates in enum order.
 */
export const RELATIONSHIPS = [
  { key: "father", label: "Father" },
  { key: "mother", label: "Mother" },
  { key: "brother", label: "Brother" },
  { key: "sister", label: "Sister" },
  { key: "maternal-grandmother", label: "Maternal grandmother" },
  { key: "maternal-grandfather", label: "Maternal grandfather" },
  { key: "paternal-grandmother", label: "Paternal grandmother" },
  { key: "paternal-grandfather", label: "Paternal grandfather" },
  { key: "other", label: "Other" },
];

// Canonical family-tree sequence used ONLY by the table renderer — parents →
// maternal grandparents → paternal grandparents → siblings → other. Kept
// intentionally separate from RELATIONSHIPS so the dropdown ordering can stay
// as designed independent of table ordering.
const TABLE_ORDER = [
  "father",
  "mother",
  "maternal-grandfather",
  "maternal-grandmother",
  "paternal-grandfather",
  "paternal-grandmother",
  "brother",
  "sister",
  "other",
];
const RELATIONSHIP_PRIORITY = TABLE_ORDER.reduce((m, k, i) => {
  m[k] = i;
  return m;
}, {});

/**
 * Return a shallow-sorted copy of the relatives list ordered by the canonical
 * relationship sequence. Ties within the same relationship (multiple brothers
 * / sisters / others) preserve the original insertion order so numbering stays
 * stable — Brother 1 remains Brother 1 even after re-sort.
 */
export const sortRelativesByRelationship = (list) => {
  if (!Array.isArray(list) || list.length <= 1) return list || [];
  return list
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const pa = RELATIONSHIP_PRIORITY[a.r.relationship];
      const pb = RELATIONSHIP_PRIORITY[b.r.relationship];
      const da = pa === undefined ? Number.MAX_SAFE_INTEGER : pa;
      const db = pb === undefined ? Number.MAX_SAFE_INTEGER : pb;
      if (da !== db) return da - db;
      return a.i - b.i;
    })
    .map(({ r }) => r);
};

// Sibling relationships whose display label gets numbered when the patient
// has more than one recorded (Brother 1 / Brother 2 / …).
const NUMBERED_RELATIONSHIPS = new Set(["brother", "sister"]);

/**
 * Table label for a relative.
 *
 * - `other` falls through to whatever the user typed as `customRelation`.
 * - `brother` / `sister` get a numeric suffix (Brother 1 / Brother 2 / …) when
 *   the patient has multiple recorded — pass the full `siblings` array so the
 *   index can be computed. Called with a single argument, the plain label is
 *   returned so pre-existing usages that don't have list context still work.
 */
export const relationshipLabel = (relative, siblings) => {
  if (!relative) return "";
  if (relative.relationship === "other") {
    return (relative.customRelation || "").trim() || "Other";
  }
  const hit = RELATIONSHIPS.find((r) => r.key === relative.relationship);
  const base = hit ? hit.label : relative.relationship;

  if (
    Array.isArray(siblings) &&
    NUMBERED_RELATIONSHIPS.has(relative.relationship)
  ) {
    // Only number when there are 2+ siblings of the same kind. Preserves the
    // input order so the numbering is stable across renders.
    const sameKind = siblings.filter((s) => s.relationship === relative.relationship);
    if (sameKind.length > 1) {
      const idx = sameKind.findIndex((s) => s.relativeId === relative.relativeId);
      if (idx >= 0) return `${base} ${idx + 1}`;
    }
  }
  return base;
};

// Relationships that can be added multiple times per patient. Everyone else
// (Father / Mother / all four grandparents) may appear at most once — the
// modal blocks saving a duplicate.
const MULTI_RELATIONSHIPS = new Set(["brother", "sister", "other"]);

export const isMultiRelationship = (key) => MULTI_RELATIONSHIPS.has(key);
export const isSingleRelationship = (key) => !MULTI_RELATIONSHIPS.has(key);

// Legacy helper — kept because callers still import it. Parents are one
// specific case of the broader single-only rule now.
export const isParentRelationship = (key) => key === "father" || key === "mother";

/**
 * Common conditions quick-picks in the modal (Figma editor 2094:12171). Each
 * entry carries its ICD-10 code so a click behaves identically to picking the
 * same condition from the ICD fuzzy-search dropdown — the resulting chip has
 * source="icd" + icdCode, and RelativeForm's dedup predicate (which keys on
 * icdCode) surfaces the "already added" pill on the corresponding ICD row.
 *
 * Kept static in the frontend because the list is small, stable, and doesn't
 * need admin editing yet — if it ever needs to grow or be admin-managed, lift
 * to api-family-history-v2-service alongside RELATIONSHIPS.
 */
export const COMMON_CONDITIONS = [
  { name: "Type 2 diabetes mellitus",       icdCode: "E11",    icdValue: "ICD3956" },
  { name: "Essential (primary) hypertension", icdCode: "I10",  icdValue: "ICD10444" },
  { name: "Other hyperlipidemia",           icdCode: "E78.4",  icdValue: "ICD4490" },
  { name: "Overweight and obesity",         icdCode: "E66",    icdValue: "ICD4277" },
  { name: "Prediabetes",                    icdCode: "R73.03", icdValue: "ICD26272" },
];
